import { useEffect, useRef, useState } from 'react'

import type { LiveQuote } from '@/lib/types'

/**
 * Affichage du prix courant, bid et ask.
 *
 * Le prix clignote brièvement en vert ou en rouge selon le sens du mouvement.
 * C'est le seul effet animé de l'application : sur un prix qui change, la
 * direction se lit plus vite qu'en comparant deux nombres.
 */
export function QuoteTicker({
  quote,
  digits,
  compact = false,
}: {
  quote: LiveQuote | null
  digits: number
  /**
   * Variante d'une seule ligne, glissée à côté du nom de la paire.
   *
   * Sur téléphone, le nom, l'intervalle et la cotation occupaient trois
   * bandeaux empilés — près d'un tiers de l'écran avant la première bougie, et
   * le bouton d'achat repoussé sous la ligne de flottaison. La cotation
   * remonte donc sur la ligne du nom, et ne garde que ce qui se décide d'un
   * coup d'œil : le prix, et le spread.
   */
  compact?: boolean
}) {
  const [flash, setFlash] = useState<'up' | 'down' | null>(null)
  const previous = useRef<number | null>(null)

  useEffect(() => {
    if (!quote) return
    const mid = Number(quote.mid)
    if (previous.current !== null && mid !== previous.current) {
      setFlash(mid > previous.current ? 'up' : 'down')
      const timer = window.setTimeout(() => setFlash(null), 400)
      previous.current = mid
      return () => window.clearTimeout(timer)
    }
    previous.current = mid
  }, [quote])

  if (!quote) {
    return compact ? (
      <span className="num text-[12px] text-ink-muted">—</span>
    ) : (
      <div className="flex items-baseline gap-3 px-3 py-2 text-[13px] text-ink-muted">
        <span className="num">Aucune cotation</span>
      </div>
    )
  }

  const tone = flash === 'up' ? 'text-up' : flash === 'down' ? 'text-down' : 'text-ink-strong'

  if (compact) {
    return (
      <span className="flex min-w-0 items-baseline justify-end gap-2">
        <span className={`num truncate text-[17px] font-semibold transition-colors ${tone}`}>
          {trim(quote.mid, digits)}
        </span>
        <span className="num shrink-0 text-[10px] text-ink-muted">
          sp {trim(quote.spread, digits)}
        </span>
      </span>
    )
  }

  return (
    <div className="flex items-baseline justify-between gap-3 px-3 py-2">
      <div className="flex items-baseline gap-3">
        <span className={`num text-[20px] font-semibold transition-colors ${tone}`}>
          {trim(quote.mid, digits)}
        </span>
        <span className="num text-[11px] text-ink-muted">
          <span className="text-down">{trim(quote.bid, digits)}</span>
          {' / '}
          <span className="text-up">{trim(quote.ask, digits)}</span>
        </span>
      </div>
      <span className="num text-[11px] text-ink-muted">spread {trim(quote.spread, digits)}</span>
    </div>
  )
}

function trim(value: string, digits: number): string {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed.toFixed(digits) : value
}
